import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

/** What the certificate says; the page and the PDF render the same fields. */
export interface ICertificateContent {
  name: string;
  hospitalName: string;
  /** "October 21, 2026". */
  certifiedDate: string;
  curriculumVersion: number | null;
}

/**
 * Worded so it is also true for a user a SuperAdmin marked trained live (waived), who never took the
 * knowledge check.
 */
export const CERTIFICATE_BODY =
  "has completed the Clear Health front-desk training.";

// Brand purple (--brand-purple-700) and grays; the PDF cannot read CSS variables.
const PURPLE = "#4f46e5";
const GRAY = "#706f7b";

const styles = StyleSheet.create({
  page: {
    padding: 36,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
  },
  frame: {
    flexGrow: 1,
    borderWidth: 3,
    borderColor: PURPLE,
    borderRadius: 12,
    padding: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  ribbon: {
    width: 84,
    height: 84,
    borderRadius: 42,
    backgroundColor: PURPLE,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  ribbonText: {
    color: "#ffffff",
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
  },
  eyebrow: {
    fontSize: 12,
    color: GRAY,
    letterSpacing: 2,
    marginBottom: 8,
  },
  title: {
    fontSize: 36,
    fontFamily: "Helvetica-Bold",
    color: PURPLE,
    marginBottom: 24,
  },
  certifies: {
    fontSize: 13,
    color: GRAY,
    marginBottom: 6,
  },
  name: {
    fontSize: 28,
    fontFamily: "Helvetica-Bold",
    marginBottom: 10,
  },
  hospital: {
    fontSize: 15,
    marginBottom: 22,
  },
  body: {
    fontSize: 12,
    color: GRAY,
    textAlign: "center",
    maxWidth: 420,
    marginBottom: 22,
  },
  date: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
  },
  version: {
    position: "absolute",
    bottom: 16,
    right: 20,
    fontSize: 8,
    color: GRAY,
  },
});

/**
 * CLE-3015 — the Clear Certified certificate as a PDF (Download PDF on the certificate page). Rendered in
 * the browser with @react-pdf/renderer, from the same fields as the page (spec §4 as built: no PDF on the
 * API). The ribbon is a purple disc until John's ribbon artwork exists.
 */
export function CertificatePdf({
  name,
  hospitalName,
  certifiedDate,
  curriculumVersion,
}: ICertificateContent) {
  return (
    <Document title="Clear Certified certificate" author="Clear Health">
      <Page size="LETTER" orientation="landscape" style={styles.page}>
        <View style={styles.frame}>
          <View style={styles.ribbon}>
            <Text style={styles.ribbonText}>{"CLEAR\nCERTIFIED"}</Text>
          </View>
          <Text style={styles.eyebrow}>CERTIFICATE OF COMPLETION</Text>
          <Text style={styles.title}>Clear Certified</Text>
          <Text style={styles.certifies}>This certifies that</Text>
          <Text style={styles.name}>{name}</Text>
          {hospitalName ? (
            <Text style={styles.hospital}>{hospitalName}</Text>
          ) : null}
          <Text style={styles.body}>{CERTIFICATE_BODY}</Text>
          <Text style={styles.date}>{certifiedDate}</Text>
          {curriculumVersion ? (
            <Text style={styles.version}>
              Curriculum version {curriculumVersion}
            </Text>
          ) : null}
        </View>
      </Page>
    </Document>
  );
}
